import React from 'react';
import bemBlock from '@gik/core/utils/bemBlock';
import RecipientInfoForm, { IRecipientInfoFormValues } from './RecipientInfoForm';
import RecipientInfoAddressForm, {
  IRecipientInfoAddressFormValues,
  recipientInfoAddressFormSchema,
} from './RecipientInfoAddressForm';
import { Form, FormButtons, FormProps, PageSection } from '@gik/ui';
import { FormRenderProps } from 'react-final-form';
import { recipientInfoFormSchema } from '@gik/create/formSchemas';
import { ValidationErrors } from 'final-form';
import { validateForm, ValidationOptions } from '@gik/ui/Form/validation';
import { CreatePageContext } from '@gik/create/components/CreatePage/CreatePage';
import { useCreateFlowStore } from '@gik/create';
import { RecipientType } from '@gik/create/enums/RecipientType';
import { CreateAnalyticsEvents } from '@gik/create/hooks/useCreateAnalytics';
import { AboutStepInputNames } from '@gik/create/enums/AboutStepInputNames';
import { PageNameStepInputNames } from '@gik/create/enums/PageNameStepInputNames';

export interface IRecipientInfoStepProps extends FormProps {
  buttons?: (form: FormRenderProps, formId: string) => React.ReactNode;
  buttonsPortal?: () => HTMLElement;
  onSubmit?: (values: IRecipientFormValues) => void;
  onChange?: (values: IRecipientFormValues) => void;
}

export interface IRecipientFormValues extends IRecipientInfoFormValues, IRecipientInfoAddressFormValues {}

const blockName = 'recipient-info-step';
export const formId = 'CreateRecipientInfoStepForm';

export default function RecipientInfoStep({
  className,
  buttons,
  buttonsPortal,
  onChange,
  onSubmit,
  ...otherProps
}: IRecipientInfoStepProps) {
  const bem = bemBlock(blockName);
  const { recipientFormRef } = React.useContext(CreatePageContext);
  const formValues = useCreateFlowStore(state => state.formValues);
  const formSchema = recipientInfoFormSchema(
    formValues[AboutStepInputNames.RecipientType] === RecipientType.Organization
  ).concat(recipientInfoAddressFormSchema(formValues[AboutStepInputNames.RecipientType] === RecipientType.Myself));

  const [values, setValues] = React.useState<IRecipientInfoAddressFormValues>();

  function handleSubmit(values: IRecipientFormValues) {
    setValues(values);
    if (onSubmit) {
      onSubmit(values);
    }
  }

  function handleChange(values: IRecipientFormValues) {
    setValues(values);
    if (onChange) {
      onChange(values);
    }
  }

  function customValidateForm(values: IRecipientFormValues, opts: ValidationOptions) {
    // run the default validator
    const errors: ValidationErrors = validateForm(formSchema, values, opts);

    // remove some errors if the fields are hidden
    if (!values.includeRecipientAddress) {
      delete errors.address1;
      delete errors.address2;
      delete errors.city;
      delete errors.state;
      delete errors.postalCode;
    }

    return errors;
  }

  return (
    <Form
      ref={recipientFormRef}
      className={bem(null, null, [className, 'gik-form--white'])}
      schema={formSchema}
      initialValues={formValues}
      onSubmit={handleSubmit}
      onChange={handleChange}
      validate={customValidateForm}
      trackingId={CreateAnalyticsEvents.CREATE_STARTED}
      mutators={{
        setIncludeRecipientAddress: (args, state, utils) => {
          utils.changeValue(state, PageNameStepInputNames.PageName, () => args[0]);
        },
      }}
      vertical
      id={formId}
      render={(form: FormRenderProps) => {
        return (
          <div>
            <PageSection className={bem('recipient-info')} contained={false} variant="aqua" gradientDarkening noPad>
              <RecipientInfoForm
                isOrganization={formValues[AboutStepInputNames.RecipientType] === RecipientType.Organization}
                form={form}
              />
            </PageSection>
            <PageSection className={bem('recipient-address')} contained={false} variant="solid-neutral" noPad>
              <RecipientInfoAddressForm
                isMyself={formValues[AboutStepInputNames.RecipientType] === RecipientType.Myself}
                form={form}
                values={values}
              />
            </PageSection>

            <FormButtons buttons={buttons} buttonsPortal={buttonsPortal} form={form} formId={formId} />
          </div>
        );
      }}
      {...otherProps}
    />
  );
}
